import React, { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, Trash2, Loader2, Image as ImageIcon, Store } from 'lucide-react';

export const resolveMediaUrl = (u) => (u && u.startsWith('/') ? `${BACKEND_URL}${u}` : u);

const Slot = ({ tipo, label, hint, value, onChange, toast, shape }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/catalogo/upload-imagem?tipo=${tipo}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.url);
      toast.success(`${label} atualizado!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Falha ao enviar ${label.toLowerCase()}`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/catalogo/upload-imagem?tipo=${tipo}`);
      onChange(null);
      toast.success(`${label} removido`);
    } catch {
      toast.error('Falha ao remover');
    } finally {
      setBusy(false);
    }
  };

  const src = resolveMediaUrl(value);
  const isLogo = shape === 'logo';
  return (
    <div className="space-y-2" data-testid={`catalog-${tipo}-slot`}>
      <Label className="font-medium">{label}</Label>
      <div
        className={`relative overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center ${isLogo ? 'w-28 h-28 rounded-full' : 'w-full h-36 rounded-xl'}`}
      >
        {src ? (
          <img src={src} alt={label} className="w-full h-full object-cover" data-testid={`catalog-${tipo}-preview`} />
        ) : isLogo ? <Store className="w-8 h-8 text-slate-300" /> : <ImageIcon className="w-8 h-8 text-slate-300" />}
        {busy && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>}
      </div>
      <p className="text-xs text-slate-500">{hint}</p>
      <div className="flex gap-2">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`catalog-${tipo}-input`} />
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`catalog-${tipo}-upload-btn`}>
          <Upload className="w-4 h-4 mr-1" /> {value ? 'Trocar' : 'Enviar'}
        </Button>
        {value && (
          <Button type="button" size="sm" variant="ghost" className="text-rose-600" disabled={busy} onClick={remove} data-testid={`catalog-${tipo}-remove-btn`}>
            <Trash2 className="w-4 h-4 mr-1" /> Remover
          </Button>
        )}
      </div>
    </div>
  );
};

export const CatalogMediaUploader = ({ config, setConfig, toast }) => (
  <div className="space-y-4">
    <div>
      <Label className="text-base font-semibold">Identidade visual da sua página</Label>
      <p className="text-sm text-slate-500">Logo e imagem de tema (capa) que aparecem no topo do catálogo online.</p>
    </div>
    <div className="grid sm:grid-cols-[auto,1fr] gap-6 items-start">
      <Slot tipo="logo" label="Logo" shape="logo" hint="PNG/JPG quadrado, até 5 MB" value={config.logo} onChange={(url) => setConfig({ ...config, logo: url })} toast={toast} />
      <Slot tipo="banner" label="Imagem de tema (capa)" shape="banner" hint="Recomendado 1600×500 px, até 5 MB" value={config.banner_image} onChange={(url) => setConfig({ ...config, banner_image: url })} toast={toast} />
    </div>
  </div>
);
